import { useState, useRef, useEffect } from 'react';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface LeadData {
  nome?: string;
  cidade?: string;
  estado?: string;
  telefone?: string;
  resumo?: string;
}

const WELCOME_MESSAGE =
  'Olá! Seja bem-vindo(a) à LicitMax. Sou a Bia, concierge comercial do escritório. Posso te ajudar a entender como sua empresa pode vender para o governo com segurança jurídica. Qual o segmento da sua empresa?';

function getConversationId() {
  const existing = sessionStorage.getItem('licitmax_cid');
  if (existing) return existing;
  const id = 'cid_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  sessionStorage.setItem('licitmax_cid', id);
  return id;
}

export default function ConciergeChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lead, setLead] = useState<LeadData | null>(null);
  const [initialized, setInitialized] = useState(false);
  const conversationIdRef = useRef<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    conversationIdRef.current = getConversationId();

    const savedHistory = sessionStorage.getItem('licitmax_history');
    const savedLead = sessionStorage.getItem('licitmax_lead');
    if (savedHistory) {
      try {
        setMessages(JSON.parse(savedHistory));
      } catch {
        // ignore corrupted history
      }
    }
    if (savedLead) {
      try {
        setLead(JSON.parse(savedLead));
      } catch {
        // ignore corrupted lead
      }
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const toggleChat = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    if (willOpen && !initialized && messages.length === 0) {
      setInitialized(true);
      const welcome: ChatMessage = { role: 'assistant', content: WELCOME_MESSAGE };
      setMessages([welcome]);
      sessionStorage.setItem('licitmax_history', JSON.stringify([welcome]));
    }
  };

  const sendMessage = async () => {
    if (lead || isLoading) return;
    const trimmed = input.trim();
    if (!trimmed) return;

    const userMsg: ChatMessage = { role: 'user', content: trimmed };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    sessionStorage.setItem('licitmax_history', JSON.stringify(nextMessages));
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: nextMessages,
          conversationId: conversationIdRef.current,
          pageUrl: window.location.href,
        }),
      });
      const data = await response.json();

      if (data.error) {
        const errMsg: ChatMessage = {
          role: 'assistant',
          content: 'Desculpe, houve um erro. Tente novamente em instantes.',
        };
        setMessages((prev) => [...prev, errMsg]);
        console.error('Erro Bia:', data.error, data.detail);
        return;
      }

      if (data._meta?.lead) {
        setLead(data._meta.lead);
        sessionStorage.setItem('licitmax_lead', JSON.stringify(data._meta.lead));
      } else {
        const assistantContent =
          data.choices?.[0]?.message?.content || 'Desculpe, não consegui responder.';
        const assistantMsg: ChatMessage = { role: 'assistant', content: assistantContent };
        const withAssistant = [...nextMessages, assistantMsg];
        setMessages(withAssistant);
        sessionStorage.setItem('licitmax_history', JSON.stringify(withAssistant));
      }
    } catch (error) {
      const errMsg: ChatMessage = {
        role: 'assistant',
        content: 'Erro de conexão. Tente novamente.',
      };
      setMessages((prev) => [...prev, errMsg]);
      console.error('Erro Bia:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <button
        onClick={toggleChat}
        aria-label="Atendimento"
        className="fixed bottom-6 right-5 z-[9999] flex items-center gap-2.5 h-[58px] pl-4 pr-6 rounded-full bg-gradient-to-br from-primary to-primary-600 border border-accent shadow-[0_10px_30px_rgba(0,0,0,0.35)] hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(0,0,0,0.4)] transition-all duration-300 cursor-pointer"
      >
        <i className="ri-message-3-line text-2xl text-accent"></i>
        <span className="hidden sm:inline text-white font-semibold text-xs tracking-widest uppercase">
          Atendimento
        </span>
      </button>

      {isOpen && (
        <div className="fixed z-[9999] bottom-[94px] right-5 w-[min(420px,calc(100vw-32px))] h-[min(650px,calc(100vh-124px))] sm:bottom-[94px] max-sm:bottom-[84px] max-sm:right-4 max-sm:w-[calc(100vw-32px)] max-sm:h-[calc(100svh-110px)] bg-light-grey rounded-3xl shadow-[0_30px_70px_rgba(0,0,0,0.4)] border border-accent/40 flex flex-col overflow-hidden font-sans">
          <div className="bg-gradient-to-br from-primary to-primary-600 px-6 py-5 flex justify-between items-center border-b-2 border-accent">
            <div>
              <h3 className="text-white text-lg font-semibold">LicitMax Consultoria</h3>
              <span className="text-accent text-[11px] uppercase tracking-wider opacity-90">
                Online agora
              </span>
            </div>
            <button
              onClick={toggleChat}
              aria-label="Fechar atendimento"
              className="text-white text-2xl leading-none cursor-pointer hover:text-accent transition-colors"
            >
              ×
            </button>
          </div>

          <div
            role="log"
            aria-live="polite"
            className="flex-1 overflow-y-auto overscroll-contain p-4 flex flex-col gap-3 bg-light-grey"
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={
                  msg.role === 'user'
                    ? 'self-end max-w-[75%] bg-accent text-primary-600 font-medium text-sm p-3.5 rounded-tl-2xl rounded-tr-md rounded-b-2xl whitespace-pre-wrap break-words'
                    : 'self-start max-w-[80%] bg-white text-[#1F2937] text-sm p-3.5 rounded-tr-2xl rounded-tl-md rounded-b-2xl border border-black/5 whitespace-pre-wrap break-words'
                }
              >
                {msg.content}
              </div>
            ))}

            {isLoading && (
              <div className="self-start max-w-[80%] bg-white text-[#1F2937] text-sm p-3.5 rounded-tr-2xl rounded-tl-md rounded-b-2xl border border-black/5">
                Escrevendo…
              </div>
            )}

            {lead && (
              <div className="self-stretch bg-gradient-to-br from-primary to-primary-600 border border-accent rounded-2xl p-5 shadow-lg animate-[fadeSlideUp_0.6s_cubic-bezier(0.16,1,0.3,1)_forwards]">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-checkbox-circle-line text-accent text-lg"></i>
                  <span className="text-white font-semibold text-sm">Contato Registrado</span>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between gap-3 text-[13px]">
                    <span className="text-white/60">Cliente</span>
                    <span className="text-white font-medium text-right">{lead.nome || '—'}</span>
                  </div>
                  {(lead.cidade || lead.estado) && (
                    <div className="flex justify-between gap-3 text-[13px]">
                      <span className="text-white/60">Cidade/UF</span>
                      <span className="text-white font-medium text-right">
                        {[lead.cidade, lead.estado].filter(Boolean).join('/')}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between gap-3 text-[13px]">
                    <span className="text-white/60">WhatsApp</span>
                    <span className="text-white font-medium text-right">{lead.telefone || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-3 text-[13px]">
                    <span className="text-white/60">Segmento</span>
                    <span className="text-white font-medium text-right">{lead.resumo || '—'}</span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-dashed border-white/20 flex items-center gap-2 text-[12px] text-white/70">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-400 shadow-[0_0_10px_#4ade80] flex-shrink-0"></span>
                  <span>A equipe da LicitMax entrará em contato em breve pelo WhatsApp.</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 bg-white flex gap-3 border-t border-black/5">
            <label htmlFor="concierge-input" className="sr-only">
              Mensagem para Bia
            </label>
            <textarea
              id="concierge-input"
              rows={1}
              value={input}
              disabled={!!lead}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={lead ? 'Registro concluído' : 'Como posso ajudar?'}
              aria-label="Mensagem para Bia"
              className="flex-1 resize-none rounded-xl border border-black/10 px-3.5 py-2.5 text-sm text-[#1F2937] focus:outline-none focus:border-primary disabled:bg-light-grey disabled:cursor-not-allowed"
            />
            <button
              onClick={sendMessage}
              disabled={!!lead || isLoading}
              aria-label="Enviar"
              className="w-[50px] flex-shrink-0 flex items-center justify-center rounded-xl bg-accent text-primary hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-send-plane-fill text-lg"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
